import { useRef, useState } from "react";

import { IoHelpCircleOutline } from "react-icons/io5";

import { cn } from "@/utils/cn";

interface GuideProps {
  title: string;
  content: string[];
}

export const GuideButton = ({ title, content }: GuideProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        className="text-gray-400 transition-colors hover:text-primary"
        aria-label={title}
      >
        <IoHelpCircleOutline size={22} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[340px] rounded-lg border border-gray-200 bg-white shadow-lg md:w-[400px]">
          <div className="rounded-t-lg bg-primary px-4 py-2.5">
            <span className="text-sm font-medium text-white">{title}</span>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-4">
            <ol className="list-inside list-decimal space-y-1.5 text-xs leading-5 text-gray-600">
              {content.map((text, i) => (
                <li key={i}>{text}</li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

interface PageTitleProps {
  subtitle?: string;
  title: string;
  guide?: GuideProps;
  className?: string;
}

export const PageTitle = ({ subtitle, title, guide, className }: PageTitleProps) => (
  <div className={cn("mb-6 flex items-center justify-between", className)}>
    <div className="flex flex-col gap-1">
      {subtitle && (
        <h1 className="text-small font-medium tracking-wide text-primary/70">{subtitle}</h1>
      )}
      <h2 className="text-2xl font-bold text-black">{title}</h2>
    </div>
    {guide && <GuideButton title={guide.title} content={guide.content} />}
  </div>
);
